import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alchemy, Network } from "alchemy-sdk";

import ArtworkCertificate from "../../components/Features/ArtworkCertificate/ArtworkCertificate";
import NFTCertificate from "../../components/Features/NFTCertificate/NFTCertificate";
import CertificatePage from "../../components/Features/CertificatePage/CertificatePage";
import CertificateInfoSection from "../../components/Features/CertificateInfoSection/CertificateInfoSection";

import { hostURL } from "../../services/data_requests";
import {
  getCollectionMetadata,
  getNFTMetadata,
} from "../../components/Features/Market/Market";

const alchemy = new Alchemy({
  apiKey: "m41T5ThjocusPU5g2OthxFelIYDnECJc",
  network: Network.ETH_MAINNET,
});

const unwrapMetadata = (value) => {
  let current = value;

  for (let depth = 0; depth < 3; depth += 1) {
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      return {};
    }

    if (
      [
        "name",
        "description",
        "image",
        "image_url",
        "imageUrl",
        "attributes",
        "traits",
        "properties",
      ].some((key) => current[key] !== undefined)
    ) {
      return current;
    }

    current = current.metadata || current.data || current.json;
  }

  return current && typeof current === "object" ? current : {};
};

const normalizeAttributes = (metadata) => {
  const source = metadata?.attributes || metadata?.traits || metadata?.properties;
  const toDisplayValue = (value) =>
    value === null || value === undefined
      ? ""
      : typeof value === "object"
        ? JSON.stringify(value)
        : String(value);

  if (Array.isArray(source)) {
    return source
      .map((attribute) => ({
        trait_type: String(
          attribute?.trait_type || attribute?.name || attribute?.key || "",
        ),
        value: toDisplayValue(
          attribute?.value ??
            attribute?.display_value ??
            attribute?.description ??
            "",
        ),
      }))
      .filter((attribute) => attribute.trait_type && attribute.value !== "");
  }

  if (source && typeof source === "object") {
    return Object.entries(source).map(([trait_type, value]) => ({
      trait_type,
      value: toDisplayValue(value),
    }));
  }

  return [];
};

const firstText = (...values) =>
  values.find(
    (value) =>
      typeof value === "string" &&
      value.trim() &&
      !["null", "undefined"].includes(value.trim().toLowerCase()),
  ) || "";

const Certificate = () => {
  const { contractAddress, tokenId, seed } = useParams();

  const [loading, setLoading] = useState(true);
  const [seedData, setSeedData] = useState(null);

  const [nftData, setNftData] = useState(null);
  const [marketMeta, setMarketMeta] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [contractMeta, setContractMeta] = useState(null);

  const isSeed = !!seed;
  const isContract = !!contractAddress && !!tokenId;

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setSeedData(null);
        setNftData(null);
        setMarketMeta(null);
        setContractData(null);
        setContractMeta(null);

        let finalContract = contractAddress;
        let finalTokenId = tokenId;

        if (isSeed) {
          const res = await fetch(`${hostURL}certificates/${seed}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "omit",
          });

          if (!res.ok) {
            throw new Error("Failed to fetch seed certificate");
          }

          const data = await res.json();

          console.log("SEED DATA:", data);

          setSeedData(data);

          finalContract = data.contract_address;

          finalTokenId =
            typeof data.token_id === "string" && data.token_id.startsWith("0x")
              ? parseInt(data.token_id, 16).toString()
              : data.token_id;
        }

        const alchemyRes = await alchemy.nft.getNftMetadata(
          finalContract,
          finalTokenId,
        );

        console.log("ALCHEMY NFT:", alchemyRes);

        setNftData(alchemyRes);

        const tokenUri =
          typeof alchemyRes?.tokenUri === "string"
            ? alchemyRes.tokenUri
            : alchemyRes?.tokenUri?.raw || alchemyRes?.tokenUri?.gateway;

        if (tokenUri) {
          const meta = await getNFTMetadata(tokenUri);

          console.log("MARKET META:", meta);

          setMarketMeta(meta);
        }

        try {
          const contractRes = await alchemy.nft.getContractMetadata(finalContract);
          setContractData(contractRes);

          const contractUri =
            contractRes?.contractMetadata?.contractUri ||
            contractRes?.contractMetadata?.contractURI ||
            contractRes?.contractUri ||
            contractRes?.contractURI;

          if (contractUri) {
            setContractMeta(await getCollectionMetadata(contractUri));
          }
        } catch (error) {
          console.warn("Collection metadata unavailable:", error);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isSeed || isContract) {
      fetchAllData();
    }
  }, [seed, contractAddress, tokenId, isSeed, isContract]);

  if (loading) return <p>Loading...</p>;

  if (!nftData) return <p>Error loading NFT</p>;

  const metadata1 = unwrapMetadata(
    nftData?.raw?.metadata || nftData?.rawMetadata || nftData?.raw,
  );
  const metadata2 = unwrapMetadata(marketMeta?.raw || marketMeta);
  const collectionMetadata = unwrapMetadata(contractMeta?.raw || contractMeta);
  const attributes = Array.from(
    new Map(
      [metadata1, metadata2, collectionMetadata]
        .flatMap(normalizeAttributes)
        .map((attribute) => [
          `${attribute.trait_type.toLowerCase()}:${attribute.value}`,
          attribute,
        ]),
    ).values(),
  );
  const collectionSources = [
    contractData?.openSeaMetadata,
    contractData?.contractMetadata?.openSeaMetadata,
    nftData?.contract?.openSeaMetadata,
    nftData?.collection,
    metadata1?.collection,
    metadata1?.contract,
    metadata2?.collection,
    metadata2?.contract,
    collectionMetadata,
  ].filter(Boolean);
  const collectionName = firstText(
    ...collectionSources.flatMap((source) => [source.collectionName, source.name]),
    contractData?.contractMetadata?.name,
    nftData?.contract?.name,
  );
  const collectionDescription = firstText(
    ...collectionSources.map((source) => source.description),
  );

  const gateways = [
  "https://funooni-public.mypinata.cloud/ipfs/",
  "https://ipfs.io/ipfs/",
  "https://gateway.pinata.cloud/ipfs/",
  "https://cloudflare-ipfs.com/ipfs/",
];

const safeImage = (img, gatewayIndex = 0) => {
  if (!img) return "";

  const imageUri =
    typeof img === "string"
      ? img
      : img.cachedUrl ||
        img.thumbnailUrl ||
        img.pngUrl ||
        img.gateway ||
        img.url ||
        img.originalUrl ||
        "";

  if (
    typeof imageUri !== "string" ||
    !imageUri.trim() ||
    ["null", "undefined"].includes(imageUri.trim().toLowerCase())
  ) {
    return "";
  }

  if (imageUri.startsWith("ipfs://")) {
    const hash = imageUri.replace(/^ipfs:\/\/(?:ipfs\/)?/, "");
    return `${gateways[gatewayIndex]}${hash}`;
  }

  if (/^(Qm|bafy)/i.test(imageUri)) {
    return `${gateways[gatewayIndex]}${imageUri}`;
  }

  return imageUri;
};

  const name = firstText(
    nftData?.name,
    metadata1?.name,
    metadata2?.name,
    nftData?.contract?.name,
    "No name",
  );

  const description = firstText(
    nftData?.description,
    metadata1?.description,
    metadata2?.description,
  );

  const image = [
    nftData?.image,
    metadata1?.image,
    metadata1?.image_url,
    metadata1?.imageUrl,
    metadata1?.imageURI,
    metadata2?.image,
  ]
    .map((candidate) => safeImage(candidate))
    .find(Boolean);

  const collectionImage = collectionSources
    .flatMap((source) => [
      source.imageUrl,
      source.image_url,
      source.image,
      source.logo,
      source.logoUrl,
      source.featuredImageUrl,
      source.bannerImageUrl,
    ])
    .map((candidate) => safeImage(candidate))
    .find(Boolean);

  const getTrait = (...keys) => {
    const normalizedKeys = keys.map((key) => key.toLowerCase());
    return attributes.find((attribute) =>
      normalizedKeys.includes(attribute.trait_type.toLowerCase()),
    )?.value;
  };

  const visibleAttributes = attributes.filter(
    (attribute) =>
      !["year", "size"].includes(attribute.trait_type.toLowerCase()) &&
      attribute.value !== null &&
      attribute.value !== undefined &&
      attribute.value !== "",
  );

  const tokenType = nftData?.tokenType || "Unknown";

  const quantity =
    nftData?.contract?.totalSupply ??
    metadata1?.quantity ??
    metadata2?.quantity ??
    nftData?.quantity ??
    null;

  const owner = nftData?.contract?.contractDeployer || "Unknown";

  const images = [image, image, image, image, image];

  const note = isSeed
    ? ""
    : "*This certificate was authorized via a link and is not a direct certificate for this artwork.";

  return (
    <>
      <CertificatePage images={images} note={note} />

      <ArtworkCertificate title={name} description={description} />

      {seedData && (
        <CertificateInfoSection
          title={collectionName || "Collection"}
          description={collectionDescription}
          collectionImage={collectionImage || ""}
          serialNumber={seedData?.certificate_serial_number}
          edition={seedData?.number_of_edition}
          issuedBy={seedData?.issued_by}
          date={seedData?.datetime}
        />
      )}

      <NFTCertificate
        image={image}
        data={{
          name,
          year:
            getTrait("Year", "Creation Year") || metadata1?.year || metadata2?.year,
          size:
            getTrait("Size", "Dimensions") || metadata1?.size || metadata2?.size,
          description,
          issueAddress: contractAddress || nftData?.contract?.address || owner,
          ownerAddress: owner,
          tokenId: nftData?.tokenId,
          tokenType: tokenType,
          tokenSupply: quantity,
          attributes: visibleAttributes,
        }}
      />
    </>
  );
};

export default Certificate;
